import {
    ChevronRight,
} from "lucide-react";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { StaggerInView, StaggerInViewItem } from "../tools/Animations";
import { HealthSection } from "../health/HealthSection";
import { getLocalized, HealthLang, LocalizedString } from "../health/healthTypes";
import {
    HEALTH_BG_GREY,
    healthClinicalJourneyAccent,
    healthClinicalJourneyCard,
    healthClinicalJourneyCardStageTitle,
    healthClinicalJourneyCardSummary,
    healthClinicalJourneyHeader,
    healthClinicalJourneyHeaderStack,
    healthClinicalJourneyIntro,
    healthClinicalJourneyMobileNode,
    healthClinicalJourneyMobileStep,
    healthClinicalJourneySectionWrap,
    healthClinicalJourneyStageColumn,
    healthClinicalJourneyStageShell,
    healthClinicalJourneyTimelineConnector,
    healthClinicalJourneyTimelineEndDot,
    healthClinicalJourneyTimelineLine,
    healthClinicalJourneyTimelineNode,
    healthClinicalJourneyTimelineRail,
    healthClinicalJourneyTimelineRailArrow,
    healthClinicalJourneyTimelineStage,
    healthClinicalJourneyTitle,
    healthSectionEyebrow,
} from "../health/healthVisualStyles";

const SECTION_ID = "learning-journey";

function timelineArrowOffsets(phaseCount: number): string[] {
    if (phaseCount < 2) return [];
    return Array.from(
        { length: phaseCount - 1 },
        (_, index) => `${((index + 1) / phaseCount) * 100}%`
    );
}

function LearningLoopTimelineRail({ phaseCount }: { phaseCount: number }) {
    const offsets = timelineArrowOffsets(phaseCount);

    return (
        <div className={healthClinicalJourneyTimelineRail} aria-hidden="true">
            <span className={healthClinicalJourneyTimelineEndDot} />
            <span className={healthClinicalJourneyTimelineLine}>
                {offsets.map((left) => (
                    <ChevronRight
                        key={left}
                        className={healthClinicalJourneyTimelineRailArrow}
                        style={{ left }}
                        strokeWidth={2.25}
                    />
                ))}
            </span>
            <span className={healthClinicalJourneyTimelineEndDot} />
        </div>
    );
}

interface EducationLearningLoopSectionProps {
    data: DataKeys;
}

export function EducationLearningLoopSection({
    data,
}: EducationLearningLoopSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const eyebrow = getLocalized(data.eyebrow, hLang);
    const title = getLocalized(data.title, hLang);
    const intro = getLocalized(data.intro, hLang);
    const phases = (data.phases as DataKeys[]) ?? [];

    return (
        <HealthSection
            id={SECTION_ID}
            ariaLabelledBy={`${SECTION_ID}-title`}
            childPage
            compact
            style={{ backgroundColor: HEALTH_BG_GREY }}
        >
            <div className={healthClinicalJourneySectionWrap}>
                <header className={healthClinicalJourneyHeader}>
                    <div className={healthClinicalJourneyHeaderStack}>
                        <p className={healthSectionEyebrow}>{eyebrow}</p>
                        <span
                            className={healthClinicalJourneyAccent}
                            aria-hidden="true"
                        />
                        <h2 id={`${SECTION_ID}-title`} className={healthClinicalJourneyTitle}>
                            {title}
                        </h2>
                        <p className={healthClinicalJourneyIntro}>{intro}</p>
                    </div>
                </header>

                <div className={healthClinicalJourneyStageShell}>
                    <LearningLoopTimelineRail phaseCount={phases.length} />

                    <StaggerInView
                        className="relative z-[1] grid w-full min-w-0 grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-4"
                        stagger={0.06}
                    >
                        {phases.map((phase, index) => {
                            const stageLabel = getLocalized(
                                phase.label as LocalizedString,
                                hLang
                            );
                            const stepLabel = String(index + 1).padStart(2, "0");

                            return (
                                <StaggerInViewItem
                                    key={phase.id as string}
                                    direction="bottom"
                                >
                                    <div className={healthClinicalJourneyStageColumn}>
                                        <div className={healthClinicalJourneyTimelineStage}>
                                            <div className={healthClinicalJourneyTimelineNode}>
                                                {stepLabel}
                                            </div>
                                        </div>

                                        <div className={healthClinicalJourneyMobileStep}>
                                            <div className={healthClinicalJourneyMobileNode}>
                                                {stepLabel}
                                            </div>
                                            <div
                                                className="h-5 w-px border-l border-dashed border-teal-300/90"
                                                aria-hidden="true"
                                            />
                                        </div>

                                        <div
                                            className={healthClinicalJourneyTimelineConnector}
                                            aria-hidden="true"
                                        />

                                        <article
                                            className={`${healthClinicalJourneyCard} lg:p-5`}
                                        >
                                            <h3
                                                className={`${healthClinicalJourneyCardStageTitle} text-[1.0625rem] lg:text-[1.125rem]`}
                                            >
                                                {stageLabel}
                                            </h3>
                                            <p
                                                className={`${healthClinicalJourneyCardSummary} mt-2 text-sm leading-relaxed`}
                                            >
                                                {getLocalized(
                                                    phase.summary as LocalizedString,
                                                    hLang
                                                )}
                                            </p>
                                        </article>
                                    </div>
                                </StaggerInViewItem>
                            );
                        })}
                    </StaggerInView>
                </div>
            </div>
        </HealthSection>
    );
}
